import { useRef, useEffect, useCallback, useState } from 'react';

import { API, useListener } from '@unboared/react';

export function GameRunner({ title, src, clientID }: { title: string; src: string; clientID?: string }) {
    const iframeRef = useRef<any>(null);
    const currentScene = useRef('home');
    const [, setGameLoaded] = useState(false);
    const language = 'en';

    const type = API.isScreen() ? 'screen' : 'gamepad';
    const me = {
        uid: API.getUID(),
        username: API.getUsername(),
        avatar: API.getAvatar(),
        color: API.getColor(),
    };

    // Envoie un message au Iframe sous-jacent
    // -- ex : { }
    const postToWebView = useCallback(
        (message: any) => {
            if (!iframeRef.current) return;
            iframeRef.current.contentWindow.postMessage(message, src);
        },
        [iframeRef]
    );

    // Recois les messages du Iframe sous-jacent
    // -- ex : { }
    useEffect(() => {
        const handler = (e: any) => {
            const { data } = e;
            // if (e.origin !== src) return;
            if (data.action === '__onReady__') {
                setGameLoaded(true);
                postToWebView({
                    name: '__onReady__',
                    players: API.getGamepadIDs().map((dev) => API.getPlayer(dev)),
                    me,
                    masterID: API.getUID(API.getMasterID()),
                    scenario: {
                        game: title,
                        scenario: 'home',
                        scenarioComponent: undefined,
                    },
                    type,
                    roomID: API.getSessionID(),
                    language,
                    companyId: clientID, // Pass client ID to the game
                });
                API.emitAction('__askChildState__', { from: API.getDeviceID() });
            } else if (data.action === 'send') {
                if (data.deviceID !== '') {
                    if (data.name === '__set__') {
                        API.broadcast('__setGlobalState__', data);
                    } else {
                        API.send(data.deviceID, '__message__', data);
                    }
                } else {
                    API.emitAction('__message__', data);
                }
            } else if (data.action === 'sendServer') {
                if (data.name === 'changeScenario') {
                    API.broadcast('__changeWebviewScenario__', {
                        game: data.game,
                        scenario: data.scenario,
                    });
                }
                // sendServer(data.name, data);
            }
        };

        window.addEventListener('message', handler);

        return () => window.removeEventListener('message', handler);
    }, []);

    // Ecoute les messages arrivant des gamepads et les transfert au jeu sous-jacent
    // -- ex : { }
    useListener(
        '__message__',
        (data) => {
            postToWebView(data);
        },
        []
    );

    useListener('__setGlobalState__', (data: { game: string; scenario: string }) => {
        if (!API.isScreen(API.getDeviceID())) {
            postToWebView({
                name: '__set__',
                ...data,
            });
        }
    });

    useListener('__childState__', (data: any) => {
        postToWebView({
            name: 'changeScenario',
            game: title,
            scenario: data.scene,
        });
    });

    useListener('__askChildState__', (data: any) => {
        API.send(data.from, '__childState__', { scene: currentScene.current });
    });

    useListener(
        '__changeWebviewScenario__',
        (data: { game: string; scenario: string }) => {
            if (!API.isScreen(API.getDeviceID())) {
                postToWebView({
                    name: 'changeScenario',
                    ...data,
                });
            } else {
                currentScene.current = data.scenario;
            }
        },
        [currentScene]
    );

    useEffect(() => {
        const unsubscribe = API.onConnect((deviceID: string) => {
            postToWebView({
                name: '__onConnect__',
                uid: API.getUID(deviceID),
                username: API.getUsername(deviceID),
                avatar: API.getAvatar(deviceID),
                color: API.getColor(deviceID),
            });
            postToWebView({
                name: '__onMasterID__',
                masterID: API.getUID(API.getMasterID()),
            });
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        const unsubscribe = API.onDisconnect((deviceID: string) => {
            postToWebView({
                name: '__onDisconnect__',
                deviceID,
                masterID: API.getUID(API.getMasterID()),
            });
        });
        return unsubscribe;
    }, []);

    return <iframe ref={iframeRef} title={title} src={src} width="100%" height="100%" frameBorder="0" allow="gyroscope; accelerometer; magnetometer; autoplay; microphone" />;
}
